import { ApolloError } from '@apollo/client';

import {
  useErpFundingBoardQuery,
  useErpFundingCandidatesQuery,
  useErpFundingCandidatesTotalQuery,
  useErpFundingOperatorsTotalQuery,
  useErpFundingDictionariesQuery,
  useErpFundingGeoAliasesQuery,
  useErpFundingOperatorsQuery,
  useErpFundingPowiatsQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import {
  IBoardGroup,
  IFundingBoard,
  IFundingCandidateRow,
  IFundingOperator,
  IGeoAlias,
} from './dofinansowanieModel';

/**
 * ТЗ 0024 — read hooks for the co-financing screens.
 *
 * Grouped in one module because they are thin projections of one domain and
 * share the same fallback/refetch idiom; the mutation side lives apart in
 * `useFundingMutations`.
 *
 * Stable empty fallbacks: a fresh `[]` on every render re-triggers every
 * `useMemo`/`useEffect` that depends on the list (the same note as
 * `useCareerMembers`).
 */
const EMPTY_OPERATORS: IFundingOperator[] = [];
const EMPTY_CANDIDATES: IFundingCandidateRow[] = [];
const EMPTY_ALIASES: IGeoAlias[] = [];
const EMPTY_GROUPS: IBoardGroup[] = [];

interface IOperatorFilters {
  programType?: string;
  voivodeshipId?: string;
  powiatId?: string;
  search?: string;
  isActive?: boolean;
  pageChanged?: boolean;
  /**
   * Только операторы, чей адрес слежения выглядит не страницей новостей.
   * ⚠️ Фильтр остался в контракте, но экраном НЕ вызывается: подозрительные
   * адреса ушли в журнал прогонов вместе с прочими поломками слежения
   * (ТЗ 0054, `fundingWatchHealth`).
   */
  urlSuspect?: boolean;
  /** Что робот видит на странице: `changed` / `quiet` / `never` (ТЗ 0054).
      Разбиение полное, сегменты не пересекаются. */
  pageSegment?: string;
  /**
   * Пишет ли робот кандидатам: `confirmed` / `unconfirmed`.
   *
   * 🔴 Ось НЕЗАВИСИМАЯ от `intakePeriod`, и в этом весь смысл: период
   * отвечает «когда набор», подтверждение — «рассылаем ли». Смешение этих
   * двух вопросов в один статус владелец и назвал непонятным.
   */
  intakeConfirmation?: string;
  /** С кем работает оператор: `b2b` / `b2c` (ТЗ 0052). */
  clientType?: string;
  /** Период набора: `running` / `soon` / `rolling` / `none`. */
  intakePeriod?: string;
  limit?: number;
  offset?: number;
}

/** Страница реестра операторов. Их 124 и станет больше. */
export const OPERATORS_PAGE_SIZE = 50;

export const useFundingOperators = (filters: IOperatorFilters = {}) => {
  const { data, loading, error, refetch } = useErpFundingOperatorsQuery({
    variables: {
      programType: filters.programType || undefined,
      voivodeshipId: filters.voivodeshipId || undefined,
      powiatId: filters.powiatId || undefined,
      search: filters.search || undefined,
      isActive: filters.isActive,
      pageChanged: filters.pageChanged,
      // ⚠️ Фильтр реестра перечисляется в ЧЕТЫРЁХ местах: аргумент схемы,
      // `_filter_operators`, интерфейс выше и этот маппер. Забытое здесь
      // поле не ломает ни tsc, ни сборку — список молча показывает всё, а
      // плашка обещает тринадцать строк и приводит к ста двадцати семи.
      // Так уже было с `urlSuspect`; поймано только глазами.
      pageSegment: filters.pageSegment || undefined,
      intakeConfirmation: filters.intakeConfirmation || undefined,
      clientType: filters.clientType || undefined,
      intakePeriod: filters.intakePeriod || undefined,
      urlSuspect: filters.urlSuspect,
      // Раньше здесь стоял жёсткий `limit: 200` без единого признака обрезки
      // на экране: при росте выше двухсот строки исчезали бы молча.
      limit: filters.limit ?? OPERATORS_PAGE_SIZE,
      offset: filters.offset ?? 0,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const operators: IFundingOperator[] = data?.fundingOperators
    ? data.fundingOperators.map((row) => ({
        id: row.id,
        name: row.name,
        programType: row.programType,
        programName: row.programName,
        isActive: row.isActive,
        coverageScope: row.coverageScope,
        voivodeshipName: row.voivodeship?.name ?? null,
        powiatNames: (row.powiats || []).map((p) => p.name),
        powiatsUnresolved: row.powiatsUnresolved || [],
        maxAmount: row.maxAmount,
        maxAmountCurrency: row.maxAmountCurrency,
        ownContributionPercent: row.ownContributionPercent,
        paymentMethod: row.paymentMethod,
        decisionDays: row.decisionDays,
        rollingIntake: row.rollingIntake,
        instructionUrl: row.instructionUrl,
        instructionReady: row.instructionReady,
        newsUrl: row.newsUrl,
        // ⚠️ Маппер — ТРЕТЬЕ место, где живёт форма строки оператора, и
        // забытое здесь поле не ломает ни tsc, ни сборку: чип просто не
        // нарисуется. Поймано глазами уже дважды.
        pageState: row.pageState,
        hasConfirmedIntake: row.hasConfirmedIntake,
        clientType: row.clientType,
        intakePeriod: row.intakePeriod,
        nameSaysB2b: row.nameSaysB2b,
        pageLinksCount: row.pageLinksCount,
        urlSuspect: row.urlSuspect,
        pageChanged: row.pageChanged,
        pageChangedAt: row.pageChangedAt,
        pageHashCheckedAt: row.pageHashCheckedAt,
        candidatesCount: row.candidatesCount,
        currentIntake: row.currentIntake
          ? {
              id: row.currentIntake.id,
              startsOn: row.currentIntake.startsOn,
              endsOn: row.currentIntake.endsOn,
              status: row.currentIntake.status,
            }
          : null,
        // Окна нужны реестру, чтобы назвать в колонке ближайшее и сказать,
        // подтверждено оно или нет. `confirmedAt` здесь ОБЯЗАТЕЛЕН: без него
        // «идёт» и «идёт и рассылаем» выглядят одинаково.
        intakes: (row.intakes || []).map((intake) => ({
          id: intake.id,
          startsOn: intake.startsOn,
          endsOn: intake.endsOn,
          status: intake.status,
          // Фаза приходит с сервера: колонка и фильтр обязаны считаться одним
          // выражением, и клиентского расчёта больше нет.
          phase: intake.phase,
          daysLeft: intake.daysLeft,
          confirmedAt: intake.confirmedAt,
          confirmedByName: intake.confirmedByName,
        })),
        comment: row.comment,
        internalNote: row.internalNote,
        email: row.email,
        phone: row.phone,
      }))
    : EMPTY_OPERATORS;

  return {
    operators,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

interface ICandidateFilters {
  status?: string;
  statusGroup?: string;
  operatorId?: string;
  matchStatus?: string;
  voivodeshipId?: string;
  isDeposit?: boolean;
  isStudent?: boolean;
  includeArchived?: boolean;
  employmentStatus?: string;
  search?: string;
  limit?: number;
  offset?: number;
  /** Устарел — то же, что `operatorMatch: 'none'`. */
  needsOperatorReview?: boolean;
  /**
   * Очередь ручного подбора оператора.
   *
   * 🔴 Смысл изменён вместе с правилом (ТЗ 0067):
   * `none` / `missing` — НИ ОДНОГО активного оператора: единственное
   * состояние, где нужен человек, потому что письма такому никто не пишет.
   * `several` — активных несколько. Это уже НЕ очередь, а информационный
   * срез: письма идут от всех, выбирать одного больше не нужно.
   */
  operatorMatch?: string;
}

/**
 * Размер страницы воронки. 50 — столько же, сколько у остальных списков ERP;
 * до этого запрос брал 200 строк и печатал над ними «всего 1013», не показывая
 * ни намёка, что видно меньше пятой части.
 */
export const CANDIDATES_PAGE_SIZE = 50;

export const useFundingCandidates = (filters: ICandidateFilters = {}) => {
  const { data, loading, error, refetch } = useErpFundingCandidatesQuery({
    variables: {
      status: filters.status || undefined,
      statusGroup: filters.statusGroup || undefined,
      operatorId: filters.operatorId || undefined,
      matchStatus: filters.matchStatus || undefined,
      voivodeshipId: filters.voivodeshipId || undefined,
      isDeposit: filters.isDeposit,
      isStudent: filters.isStudent,
      includeArchived: filters.includeArchived,
      employmentStatus: filters.employmentStatus || undefined,
      needsOperatorReview: filters.needsOperatorReview || undefined,
      operatorMatch: filters.operatorMatch || undefined,
      search: filters.search || undefined,
      limit: filters.limit ?? CANDIDATES_PAGE_SIZE,
      offset: filters.offset || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const candidates: IFundingCandidateRow[] = data?.fundingCandidates
    ? data.fundingCandidates.map((row) => ({
        id: row.id,
        fullName: row.fullName,
        email: row.email,
        phone: row.phone,
        status: row.status,
        employmentStatus: row.employmentStatus,
        city: row.city,
        isDeposit: row.isDeposit,
        matchStatus: row.matchStatus,
        isStudent: row.isStudent,
        studentId: row.studentId,
        linkedUserName: row.linkedUserName,
        voivodeshipName: row.voivodeship?.name ?? null,
        powiatName: row.powiat?.name ?? null,
        powiatRaw: row.powiatRaw,
        primaryOperator: row.primaryOperator
          ? {
              id: row.primaryOperator.id,
              name: row.primaryOperator.name,
              programType: row.primaryOperator.programType,
              // ⚠️ Маппер — то самое место, где поле теряется молча: ни tsc,
              // ни сборка не заметят, а на экране чип просто не нарисуется.
              clientType: row.primaryOperator.clientType,
            }
          : null,
        operators: (row.operators || []).map((operator) => ({
          id: operator.id,
          name: operator.name,
          programType: operator.programType,
          clientType: operator.clientType,
        })),
        activeOperatorsCount: row.activeOperatorsCount ?? 0,
        disabledOperatorsCount: row.disabledOperatorsCount ?? 0,
      }))
    : EMPTY_CANDIDATES;

  return {
    candidates,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingBoard = (filters: ICandidateFilters = {}) => {
  const { data, loading, error, refetch } = useErpFundingBoardQuery({
    // 🔴 Фильтров здесь не было вовсе: сегмент «Квалификация (7)» под
    // включённым фильтром открывал ПУСТОЙ список. Число в скобках обязано
    // описывать то, что откроется по клику.
    //
    // `statusGroup`/`status` НЕ передаются намеренно — это собственное
    // измерение сегментов, и схема их не принимает: иначе выбор одной
    // группы обнулил бы остальные и переключаться стало бы нельзя.
    variables: {
      operatorId: filters.operatorId || undefined,
      matchStatus: filters.matchStatus || undefined,
      voivodeshipId: filters.voivodeshipId || undefined,
      isDeposit: filters.isDeposit,
      isStudent: filters.isStudent,
      employmentStatus: filters.employmentStatus || undefined,
      needsOperatorReview: filters.needsOperatorReview || undefined,
      operatorMatch: filters.operatorMatch || undefined,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const board: IFundingBoard = {
    total: data?.fundingBoard?.total ?? 0,
    ambiguousMatches: data?.fundingBoard?.ambiguousMatches ?? 0,
    unresolvedPlaces: data?.fundingBoard?.unresolvedPlaces ?? 0,
    depositWaiting: data?.fundingBoard?.depositWaiting ?? 0,
    pageChangedOperators: data?.fundingBoard?.pageChangedOperators ?? 0,
    suspiciousUrls: data?.fundingBoard?.suspiciousUrls ?? 0,
    operatorMissing: data?.fundingBoard?.operatorMissing ?? 0,
    submissionsNeedOperator: data?.fundingBoard?.submissionsNeedOperator ?? 0,
    groups: data?.fundingBoard?.groups
      ? data.fundingBoard.groups.map((group) => ({
          key: group.key,
          title: group.title,
          statuses: group.statuses,
          count: group.count,
        }))
      : EMPTY_GROUPS,
    stages: data?.fundingBoard?.stages
      ? data.fundingBoard.stages.map((group) => ({
          key: group.key,
          title: group.title,
          statuses: group.statuses,
          count: group.count,
        }))
      : EMPTY_GROUPS,
    buckets: data?.fundingBoard?.buckets
      ? data.fundingBoard.buckets.map((bucket) => ({
          status: bucket.status,
          count: bucket.count,
        }))
      : [],
  };

  return {
    board,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingVoivodeships = () => {
  const { data, loading } = useErpFundingDictionariesQuery({
    fetchPolicy: 'cache-first',
  });
  return {
    voivodeships: data?.fundingVoivodeships || [],
    loading,
  };
};

export const useFundingPowiats = (voivodeshipId?: string, search?: string) => {
  const { data, loading } = useErpFundingPowiatsQuery({
    variables: {
      voivodeshipId: voivodeshipId || undefined,
      search: search || undefined,
    },
    fetchPolicy: 'cache-first',
  });
  return { powiats: data?.fundingPowiats || [], loading };
};

/**
 * The import's triage queue. `unresolved` by default — these are the raw
 * coverage strings nobody has mapped onto a powiat yet, ordered by how many
 * operators use them, so the few high-impact rows come first.
 */
/** Размер пачки очереди разбора. Экспортируется, чтобы экран мог отличить
 *  «это всё» от «показана первая пачка» и сказать об этом вслух. */
export const GEO_QUEUE_LIMIT = 200;

export const useGeoQueue = (status = 'unresolved') => {
  const { data, loading, error, refetch } = useErpFundingGeoAliasesQuery({
    variables: { status, limit: GEO_QUEUE_LIMIT },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const aliases: IGeoAlias[] = data?.fundingGeoAliases
    ? data.fundingGeoAliases.map((row) => ({
        id: row.id,
        rawValue: row.rawValue,
        status: row.status,
        hits: row.hits,
        powiatName: row.powiat?.name ?? null,
        powiatKind: row.powiat?.kind ?? null,
        suggestedPowiatId: row.suggestedPowiat?.id ?? null,
        suggestedPowiatName: row.suggestedPowiat?.name ?? null,
        suggestedPowiatKind: row.suggestedPowiat?.kind ?? null,
        suggestionScore: row.suggestionScore,
        contextVoivodeshipId: row.contextVoivodeship?.id ?? null,
        contextVoivodeshipName: row.contextVoivodeship?.name ?? null,
        candidatePowiats: (row.candidatePowiats || []).map((powiat) => ({
          id: powiat.id,
          name: powiat.name,
          kind: powiat.kind,
          voivodeshipName: powiat.voivodeshipName,
        })),
      }))
    : EMPTY_ALIASES;

  return {
    aliases,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * Точное число строк под ТЕКУЩИМИ фильтрами — для пагинации.
 *
 * Отдельный запрос, а не поле в списке: список кэшируется постранично, и
 * тащить полный счёт в каждую страницу значило бы пересчитывать его на каждый
 * шаг. `fundingBoard.total` здесь не годится — он всегда про всю воронку и
 * при включённом фильтре врал бы.
 */
/**
 * Сколько всего операторов под теми же фильтрами.
 *
 * 🔴 До этого поля шапка реестра писала `operators.length` — размер страницы,
 * а не число операторов: «Операторы 50» при 124 в базе. Аргументы совпадают с
 * `useFundingOperators` один в один; расхождение здесь означало бы, что
 * счётчик и таблица отвечают на разные вопросы.
 */
export const useFundingOperatorsTotal = (filters: IOperatorFilters = {}) => {
  const { data, loading } = useErpFundingOperatorsTotalQuery({
    variables: {
      programType: filters.programType || undefined,
      voivodeshipId: filters.voivodeshipId || undefined,
      powiatId: filters.powiatId || undefined,
      search: filters.search || undefined,
      isActive: filters.isActive,
      pageChanged: filters.pageChanged,
      // ⚠️ Набор аргументов у счётчика и у списка обязан совпадать: разойдясь,
      // они ответят на разные вопросы — «требуют внимания: 12» над списком из
      // семидесяти.
      pageSegment: filters.pageSegment || undefined,
      intakeConfirmation: filters.intakeConfirmation || undefined,
      clientType: filters.clientType || undefined,
      intakePeriod: filters.intakePeriod || undefined,
      urlSuspect: filters.urlSuspect,
    },
    fetchPolicy: 'cache-and-network',
  });

  return { total: data?.fundingOperatorsTotal ?? null, loading };
};

export const useFundingCandidatesTotal = (filters: ICandidateFilters = {}) => {
  const { data, loading } = useErpFundingCandidatesTotalQuery({
    variables: {
      status: filters.status || undefined,
      statusGroup: filters.statusGroup || undefined,
      operatorId: filters.operatorId || undefined,
      matchStatus: filters.matchStatus || undefined,
      voivodeshipId: filters.voivodeshipId || undefined,
      isDeposit: filters.isDeposit,
      isStudent: filters.isStudent,
      employmentStatus: filters.employmentStatus || undefined,
      needsOperatorReview: filters.needsOperatorReview || undefined,
      operatorMatch: filters.operatorMatch || undefined,
      includeArchived: filters.includeArchived,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  return { total: data?.fundingCandidatesTotal ?? null, loading };
};
